import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { getStudents } from '../../apis/users';
import { getSchoolClassesConfig } from '../../apis/schoolConfig';
import consolidatedMarksApis from '../../apis/consolidatedMarks';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import { RootState } from '../../redux/store';
import { SchoolClassesConfig } from '../../types/schoolConfigType';
import ConsolidatedMarksReportPage from './ConsolidatedMarksReportPage';
import { User } from '../../types/userType';
import { downloadFileFromUrl } from '../../utils/fileGeneration';
import { downloadReport } from '../../utils/reportUtils';

const ConsolidatedMarksReportContainer: React.FC = () => {
  const [allClasses, setAllClasses] = useState<SchoolClassesConfig>();
  const [isClassesLoading, setIsClassesLoading] = useState<boolean>(false);
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );
  const [studentDetails, setStudentDetails] = useState<User[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadInProgress, setIsDownloadInProgress] = useState(false);

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const showToast = useCallback(
    (message: string, severity: SnackbarSeverityType) => {
      setToastMessage(message);
      setToastSeverity(severity);
      setToastOpen(true);
    },
    []
  );

  useEffect(() => {
    setIsClassesLoading(true);
    getSchoolClassesConfig(loggedInUser.school)
      .then((response) => {
        setAllClasses(response.data as unknown as SchoolClassesConfig);
      })
      .catch((error) => {
        console.error(error);
        showToast(
          'Failed to load school configuration.',
          SnackbarSeverityType.error
        );
      })
      .finally(() => setIsClassesLoading(false));
  }, [loggedInUser.school, showToast]);

  const handleSearch = useCallback(
    async (academicYear: string, grade: string, division: string) => {
      setIsLoading(true);
      try {
        // Fetch current students list
        const students = await getStudents(
          loggedInUser.school,
          academicYear,
          grade,
          division
        );
        setStudentDetails(students);
      } catch (error) {
        console.error('Failed to fetch students:', error);
        showToast('Failed to fetch students.', SnackbarSeverityType.error);
        setStudentDetails([]);
      } finally {
        setIsLoading(false);
      }
    },
    [loggedInUser.school, showToast]
  );

  const handleGenerateConsolidatedReport = useCallback(
    async (
      academicYear: string,
      grade: string,
      division: string,
      terms: string[]
    ) => {
      try {
        await consolidatedMarksApis.generateConsolidatedReport(
          academicYear,
          grade,
          division,
          terms,
          loggedInUser.school,
          loggedInUser.id
        );
        showToast(
          'Report generation started. You will be able to download it in 15 minutes.',
          SnackbarSeverityType.success
        );
      } catch (error) {
        console.error('Failed to generate consolidated report:', error);
        showToast(
          'Failed to generate consolidated report.',
          SnackbarSeverityType.error
        );
      }
    },
    [loggedInUser.school, loggedInUser.id, showToast]
  );

  const handleDownloadConsolidatedReport = useCallback(
    async (academicYear: string, grade: string, division: string) => {
      try {
        const response = await consolidatedMarksApis.getConsolidatedMarksReport(
          academicYear,
          grade,
          division,
          loggedInUser.school
        );
        downloadReport(
          response.data,
          response.fileName ||
            `Consolidated Marks Report - ${moment().format(
              'YYYY-MM-DD_HH-mm-ss'
            )}`,
          'Consolidated Marks Report',
          showToast
        );
      } catch (error) {
        console.error('Failed to download consolidated report:', error);
        showToast(
          'Failed to download consolidated report.',
          SnackbarSeverityType.error
        );
      }
    },
    [loggedInUser.school, showToast]
  );

  const handleDownloadAllReportCards = useCallback(
    async (academicYear: string, reportCardDate: string) => {
      if (!studentDetails || studentDetails.length === 0) {
        showToast(
          'No students found to download report cards for.',
          SnackbarSeverityType.success
        );
        return;
      }

      setIsDownloadInProgress(true);
      let successfulDownloads = 0;
      let failedDownloads = 0;

      for (const student of studentDetails) {
        try {
          const response = await consolidatedMarksApis.generateReportCard(
            student.id,
            academicYear,
            reportCardDate
          );
          const { reportCardUrl, fileName } = response;
          downloadFileFromUrl(reportCardUrl, fileName, 'pdf');
          successfulDownloads++;
        } catch (error) {
          console.error(
            `Failed to generate report card for student ${student.id}:`,
            error
          );
          failedDownloads++;
        }
      }
      setIsDownloadInProgress(false);

      if (successfulDownloads > 0) {
        showToast(
          `Successfully initiated download for ${successfulDownloads} report card(s).`,
          SnackbarSeverityType.success
        );
      }
      if (failedDownloads > 0) {
        showToast(
          `Failed to download ${failedDownloads} report card(s). Check console for details.`,
          SnackbarSeverityType.error
        );
      }
      if (successfulDownloads === 0 && failedDownloads === 0) {
        showToast(
          'No report cards were downloaded.',
          SnackbarSeverityType.success
        );
      }
    },
    [studentDetails, showToast]
  );

  const handleGenerateReportCard = useCallback(
    async (studentId: string, academicYear: string, reportCardDate: string) => {
      setIsDownloadInProgress(true);
      try {
        const response = await consolidatedMarksApis.generateReportCard(
          studentId,
          academicYear,
          reportCardDate
        );
        const { reportCardUrl, fileName } = response;
        downloadFileFromUrl(reportCardUrl, fileName, 'pdf');
        showToast(
          'Report card download started successfully.',
          SnackbarSeverityType.success
        );
      } catch (error) {
        console.error('Failed to generate report card:', error);
        showToast(
          'Failed to generate report card.',
          SnackbarSeverityType.error
        );
      }
      setIsDownloadInProgress(false);
    },
    [showToast]
  );

  return (
    <>
      <ConsolidatedMarksReportPage
        allClasses={allClasses as SchoolClassesConfig}
        school={loggedInUser.school}
        isClassesLoading={isClassesLoading}
        studentDetails={studentDetails}
        isLoading={isLoading}
        onSearch={handleSearch}
        onGenerateConsolidatedReport={handleGenerateConsolidatedReport}
        onDownloadConsolidatedReport={handleDownloadConsolidatedReport}
        onDownloadAllReportCards={handleDownloadAllReportCards}
        onGenerateReportCard={handleGenerateReportCard}
        isDownloadInProgress={isDownloadInProgress}
        showToast={showToast}
      />
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default ConsolidatedMarksReportContainer;
